import { NotificationGameIcon } from 'modules/notifications/components/NotificationGameIcon';
import { TournamentMatchFinishedTile } from 'modules/notifications/components/TournamentMatchFinishedTile';
import React, { FC, ReactElement, useCallback, useMemo } from 'react';
import { NotificationEntity, UserEntity } from 'app/interfaces/entity';
import { BaseNotificationTile } from 'modules/notifications/components/BaseNotificationTile';
import { NotificationType } from '@okletsplay/core';
import { Box } from '@mui/material';
import { UserAvatar } from 'shared/components/UserAvatar';

interface TournamentNotificationTileProps {
  notification: NotificationEntity;
  onClick?: (notification: NotificationEntity) => void;
}

export const TournamentNotificationTile: FC<TournamentNotificationTileProps> = ({
  notification,
  onClick = () => null,
}) => {
  const initiator = useMemo<Partial<UserEntity>>(() => notification.initiator, [notification]);
  const tournamentGameIcon = useMemo(
    () => notification.tournament?.game?.icon ?? '',
    [notification]
  );

  const teamName = useMemo(
    () =>
      notification?.tournament?.teams?.find((team) =>
        team?.players?.find((player) => player?.userId === initiator?.userId)
      )?.name ?? '',
    [initiator?.userId, notification?.tournament?.teams]
  );

  const tournamentName = useMemo(
    () => notification?.tournament?.name,
    [notification?.tournament?.name]
  );

  const notificationTemplate: Partial<Record<NotificationType, ReactElement | null>> =
    useMemo(() => {
      return {
        [NotificationType.TournamentStarted]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={notification.tournament?.game?.icon} />}
            message={notification.message ?? `${tournamentName} has started!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentCanceled]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={notification.tournament?.game?.icon} />}
            message={`${tournamentName} has been cancelled.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentAdminAdded]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={notification.tournament?.game?.icon} />}
            message={`You've been added as an admin in ${tournamentName}`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentInvitedToPlay]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={`${notification.initiator?.userName} invited you to join ${teamName} team in ${tournamentName} tournament!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentJoinRequestAccepted]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator?.userName} accepted your invite to join!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentJoinRequestDeclined]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator?.userName} declined your invite to join!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentPlayerWithdrawn]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator?.userName} left your team!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentCaptainWithdrawn]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={`Your team left ${tournamentName} tournament!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentPlayerDeleted]: (
          <BaseNotificationTile
            image={<UserAvatar user={initiator} />}
            message={`${notification.initiator?.userName} removed you from the team ${teamName} team!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentCheckinStarted]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={`${tournamentName} check-in has started!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentMatchStarted]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={notification?.message ?? `${tournamentName} Match has started!`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentRescheduled]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={notification?.message ?? `${tournamentName} has been rescheduled.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentFinished]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={notification?.message ?? `${tournamentName} has finished.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentTeamDisqualified]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={notification?.message ?? `Your team was disqualified from ${tournamentName}`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentNewRoundCheckin]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={notification?.message ?? `${tournamentName} new round will be started soon.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentMatchGameExpired]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={notification?.message ?? `${tournamentName} match game has been expired.`}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentMatchFinished]: (
          <TournamentMatchFinishedTile notification={notification} />
        ),
        [NotificationType.TournamentSpectatorInvited]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={
              notification?.message ??
              `You were invited as a spectator to ${tournamentName} tournament!`
            }
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentSpectatorJoinRequestAccepted]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={
              notification?.message ??
              `${notification.initiator?.userName} accepted your invitation to join!`
            }
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentSpectatorJoinRequestDeclined]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={
              notification?.message ??
              `${notification.initiator?.userName} declined your invitation to join!`
            }
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
        [NotificationType.TournamentAnnouncement]: (
          <BaseNotificationTile
            image={<NotificationGameIcon icon={tournamentGameIcon} />}
            message={notification?.message ?? 'New Activity'}
            date={notification.createdAt}
            seen={notification.seen}
          />
        ),
      };
    }, [initiator, notification, teamName, tournamentGameIcon, tournamentName]);

  const onClickHandler = useCallback(() => {
    onClick?.(notification);
  }, [notification, onClick]);

  if (!notification) {
    return null;
  }

  return <Box onClick={onClickHandler}>{notificationTemplate[notification.type]}</Box>;
};
